import classNames from 'classnames';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { injectIntl, intlShape } from 'react-intl';

import globalTranslations from '../../../i18n/globalTranslations';
import ProductIncomingQuantity from '../ProductIncomingQuantity';

const ProductCollectionMatrixStorageRow = ({
  displayRow,
  storageInfo,
  isLeftAligned,
  intl,
}) => {
  const renderStorageCell = (cell) => {
    const item = cell.item;

    if (!item) {
      return <td key={`storage-${storageInfo.id}-${cell.element.id}`} />;
    }

    const stockData = item.stocks.get(item.product.id);

    if (!stockData || !stockData.storages || stockData.storages.size === 0) {
      return <td key={`storage-${storageInfo.id}-${cell.element.id}`} />;
    }

    const storage = stockData.storages.get(String(storageInfo.id));

    if (!storage) {
      return <td key={`storage-${storageInfo.id}-${cell.element.id}`} />;
    }

    const hasIncoming =
      storage.free_quantity > 0 && storage.incoming_quantities?.length > 0;

    const incomingText = hasIncoming
      ? storage.incoming_quantities
          .map((incoming, index) => {
            return (
              <Fragment key={`${incoming.date}-${incoming.quantity}-${index}`}>
                <ProductIncomingQuantity
                  incomingQuantity={incoming}
                  stockUnit={stockData.stock_unit}
                />
              </Fragment>
            );
          })
          .filter(Boolean)
      : [];

    return (
      <td
        key={`storage-${storageInfo.id}-${cell.element.id}`}
        className={classNames('ProductCollectionMatrixStorageRow__cell', {
          'ProductCollectionMatrixStorageRow__cell--left-aligned':
            isLeftAligned,
        })}
      >
        <div className="ProductCollectionMatrixStorageRow__quantity-container">
          {hasIncoming && incomingText}
          <span className="ProductCollectionMatrixStorageRow__quantity">
            {storage.free_quantity} {stockData.stock_unit}
          </span>
        </div>
      </td>
    );
  };

  const getDeliveryDays = () => {
    for (const cell of displayRow.cells) {
      const item = cell.item;

      if (item) {
        const stockData = item.stocks.get(item.product.id);
        if (stockData && stockData.storages) {
          const storage = stockData.storages.get(String(storageInfo.id));

          if (storage && storage.in_stock_processing_days) {
            const { min, max } = storage.in_stock_processing_days;
            if (min === max) {
              return `${Math.round(min)} ${intl.formatMessage(
                globalTranslations.abbrDayUnit
              )}`;
            }
            return `${Math.round(min)}-${Math.round(max)} ${intl.formatMessage(
              globalTranslations.abbrDayUnit
            )}`;
          }
        }
      }
    }

    return null;
  };

  const deliveryDays = getDeliveryDays();

  return (
    <tr className="ProductCollectionMatrixStorageRow">
      <th className="ProductCollectionMatrixStorageRow__header" scope="row">
        <div className="ProductCollectionMatrixStorageRow__label">
          <span>{storageInfo.name}</span>
          {deliveryDays && (
            <span className="ProductCollectionMatrixStorageRow__delivery">
              ({deliveryDays})
            </span>
          )}
        </div>
      </th>
      {displayRow.cells.map((cell) => renderStorageCell(cell))}
    </tr>
  );
};

ProductCollectionMatrixStorageRow.propTypes = {
  storageInfo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }).isRequired,
  displayRow: PropTypes.shape({
    cells: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  intl: intlShape.isRequired,
  isLeftAligned: PropTypes.bool,
};

export default injectIntl(observer(ProductCollectionMatrixStorageRow));
